import React, { useState, useEffect, useRef } from 'react';
import { env } from '../utils/env';
import SEOHead from '../components/SEOHead';
import { 
  Mail, Phone, MapPin, Send, AlertCircle, CheckCircle2, Globe, Home, ArrowRight, MessageSquare
} from 'lucide-react';
import AnimatedSection from '../components/AnimatedSection';
import PageTransition from '../components/PageTransition';
import GradientHero from '../components/GradientHero';
import { motion } from 'framer-motion';
import { Link, useSearchParams } from 'react-router-dom';
import ReCaptcha from '../components/ReCaptcha';
import FileUpload from '../components/FileUpload';

const Contact = () => {
  const contactJsonLd = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Contact Neoqualis",
    "description": "Contactez nos experts en Testing et Business Analysis",
    "mainEntity": {
      "@type": "Organization",
      "name": "Neoqualis",
      "telephone": "+33186046182",
      "email": "contact@neoqualis.com"
    }
  };

  const [searchParams] = useSearchParams();
  const subjectParam = searchParams.get('subject');

  const [formData, setFormData] = useState({
    objet: subjectParam || '',
    prenom: '',
    nom: '',
    email: '',
    telephone: '',
    societe: '',
    message: '',
    piece_jointe: null as File | null,
    consentement_rgpd: false
  });

  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // ✅ Référence pour le scroll vers le message de confirmation/erreur
  const messageRef = useRef<HTMLDivElement>(null);

  // Pré-remplir l'objet selon le paramètre URL
  useEffect(() => {
    if (subjectParam) {
      const objetMapping: Record<string, string> = {
        'audit': 'Audit QA (30 min)',
        'projet': 'Projet / Expertise',
        'formation': 'Formation (Qualis-Academy)',
        'partenariat': 'Partenariat',
        'presse': 'Presse / Média',
        'qualis-ai': 'Qualis AI — Analyse specs & génération de tests',
        'rse': 'RSE & engagements',
        'recrutement': 'Recrutement',
        'autre': 'Autre'
      };
      
      setFormData(prev => ({
        ...prev,
        objet: objetMapping[subjectParam] || 'Autre'
      }));
    }
  }, [subjectParam]);

  // ✅ Effet pour scroller vers le message après changement d'état
  useEffect(() => {
    if ((submitSuccess || errors.submit) && messageRef.current) {
      setTimeout(() => {
        messageRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }, 100);
    }
  }, [submitSuccess, errors.submit]);

  const objetOptions = [
    'Audit QA (30 min)',
    'Projet / Expertise',
    'Formation (Qualis-Academy)',
    'Partenariat',
    'Presse / Média',
    'Qualis AI — Analyse specs & génération de tests',
    'RSE & engagements',
    'Recrutement',
    'Autre'
  ];

  // ✅ Fonction pour vérifier si une pièce jointe est autorisée
  const isPieceJointeAllowed = () => {
    return formData.objet === 'Recrutement' || formData.objet === 'Autre';
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.prenom.trim()) newErrors.prenom = 'Le prénom est requis';
    if (!formData.nom.trim()) newErrors.nom = 'Le nom est requis';
    if (!formData.email.trim()) {
      newErrors.email = "L'email est requis";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "L'email n'est pas valide";
    }
    if (!formData.objet) newErrors.objet = "L'objet est requis";
    if (!formData.message.trim()) newErrors.message = 'Le message est requis';
    if (!formData.consentement_rgpd) newErrors.consentement_rgpd = 'Le consentement RGPD est requis';
    if (!captchaToken) newErrors.captcha = 'Veuillez valider le captcha';

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // ✅ Réinitialiser la pièce jointe si l'objet change et n'autorise plus les PJ
    if (name === 'objet' && !isPieceJointeAllowed()) {
      setFormData(prev => ({ ...prev, piece_jointe: null }));
    }

    // Effacer l'erreur du champ modifié
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileChange = (file: File | null) => {
    setFormData(prev => ({ ...prev, piece_jointe: file }));
  };

  const handleCaptchaVerify = (token: string | null) => {
    setCaptchaToken(token);
    if (errors.captcha && token) {
      setErrors(prev => ({ ...prev, captcha: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (value !== null && value !== false && value !== '') {
          formDataToSend.append(key, value.toString());
        }
      });
      if (formData.piece_jointe) {
        formDataToSend.append('piece_jointe', formData.piece_jointe);
      }
      formDataToSend.append('captchaToken', captchaToken || '');

      const response = await fetch(`${env.apiUrl}/contact`, {
        method: 'POST',
        body: formDataToSend
      });

      const result = await response.json();

      if (result.success) {
        setSubmitSuccess(true);
        setFormData({
          objet: '',
          prenom: '',
          nom: '',
          email: '',
          telephone: '',
          societe: '',
          message: '',
          piece_jointe: null,
          consentement_rgpd: false
        });
        setCaptchaToken(null);
        window.grecaptcha?.reset();
      } else {
        throw new Error(result.error || "Erreur lors de l'envoi");
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setErrors({ submit: 'Une erreur est survenue. Veuillez réessayer ou nous contacter directement au +33 1 86 04 61 82.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageTransition>
      <SEOHead 
        title="Contact — Parlons de vos projets Testing & QA | Neoqualis"
        description="Contactez nos experts QA pour un audit gratuit (30 min), un projet d'expertise, une formation ou un partenariat. Réponse sous 48h ouvrées."
        canonical="https://neoqualis.com/contact"
        jsonLd={contactJsonLd}
      />

      <div className="pt-16">
        {/* Hero */}
        <GradientHero
          title="Contactez Neoqualis"
          subtitle="Parlons couverture de tests, risques et délais. Nous vous répondons sous 48 h ouvrées."
          ctas={[
            { label: "Audit QA gratuit (30 min)", to: "/audit", variant: "primary", iconRight: <MessageSquare /> },
            { label: "Voir nos expertises", to: "/expertise", variant: "secondary", iconRight: <ArrowRight /> }
          ]}
        />

        {/* Informations de contact */}
        <section className="section bg-white">
          <div className="container">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Informations de contact */}
              <AnimatedSection>
                <div className="space-y-8">
                  <div>
                    <h2 className="heading mb-6">Coordonnées</h2>
                  </div>
                  
                  <div className="space-y-6">
                    <div className="flex items-center">
                      <div className="p-3 rounded-full bg-primary bg-opacity-10">
                        <Mail className="w-6 h-6 text-primary" />
                      </div>
                      <div className="ml-4">
                        <h3 className="text-lg font-semibold text-gray-900">Email</h3>
                        <a 
                          href="mailto:contact@neoqualis.com"
                          className="text-gray-600 hover:text-primary transition-colors"
                        >
                          contact@neoqualis.com
                        </a>
                      </div>
                    </div>

                    <div className="flex items-center">
                      <div className="p-3 rounded-full bg-primary bg-opacity-10">
                        <Phone className="w-6 h-6 text-primary" />
                      </div>
                      <div className="ml-4">
                        <h3 className="text-lg font-semibold text-gray-900">Téléphone</h3>
                        <a 
                          href="tel:+33186046182"
                          className="text-gray-600 hover:text-primary transition-colors"
                        >
                          +33 1 86 04 61 82
                        </a>
                        <p className="text-sm text-gray-500">lun–ven, 9 h–18 h CET</p>
                      </div>
                    </div>

                    <div className="flex items-center">
                      <div className="p-3 rounded-full bg-primary bg-opacity-10">
                        <MapPin className="w-6 h-6 text-primary" />
                      </div>
                      <div className="ml-4">
                        <h3 className="text-lg font-semibold text-gray-900">Adresse</h3>
                        <p className="text-gray-600">2-4 Rue Vincent Van Gogh, 93360 Neuilly-Plaisance - France</p>
                        <p className="text-sm text-gray-500">
                          Locaux accessibles PMR (accès, circulation, sanitaires)
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center">
                      <div className="p-3 rounded-full bg-primary bg-opacity-10">
                        <Globe className="w-6 h-6 text-primary" />
                      </div>
                      <div className="ml-4">
                        <h3 className="text-lg font-semibold text-gray-900">Réseaux</h3>
                        <div className="flex items-center space-x-4 mt-2">
                          <a 
                            href="https://linkedin.com/company/neoqualis"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-gray-600 hover:text-blue-500 transition-colors"
                          >
                            LinkedIn
                          </a>
                          <a 
                            href="https://instagram.com/neoqualis"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-gray-600 hover:text-pink-500 transition-colors"
                          >
                            Instagram
                          </a>
                          <a 
                            href="https://facebook.com/neoqualis"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-gray-600 hover:text-blue-600 transition-colors"
                          >
                            Facebook
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </AnimatedSection>

              {/* Carte */}
              <AnimatedSection delay={0.2}>
                <div className="space-y-6">
                  <div className="card overflow-hidden">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2625.323625917258!2d2.513112876846216!3d48.85203890115295!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47e60d0e2049bedd%3A0xe7a54bac4ab6abea!2sNEOQUALIS!5e0!3m2!1sfr!2sfr!4v1758208354220!5m2!1sfr!2sfr"
                      width="100%"
                      height="300"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      className="rounded-lg"
                    ></iframe>
                  </div>
                </div>
              </AnimatedSection>
            </div>
          </div>
        </section>

        {/* Formulaire */}
        <section id="contact-form" className="section bg-gray-50">
          <div className="container">
            <div className="max-w-4xl mx-auto">
              <AnimatedSection className="text-center mb-12">
                <h2 className="heading">Formulaire de contact</h2>
                <p className="subheading">Remplissez ce formulaire et nous vous recontacterons rapidement</p>
              </AnimatedSection>

              <div className="card">
                {/* ✅ Message de confirmation avec ref pour le scroll */}
                {submitSuccess ? (
                  <motion.div
                    ref={messageRef}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-12"
                  >
                    <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-6" />
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">
                      Merci ! Votre demande a bien été envoyée.
                    </h3>
                    <p className="text-gray-700 mb-8">
                      Nous reviendrons vers vous sous 48 h ouvrées.
                    </p>
                    {/* ✅ Bouton retour à l'accueil */}
                    <Link 
                      to="/" 
                      className="btn-primary inline-flex items-center gap-2 px-6 py-3"
                    >
                      <Home className="w-5 h-5" />
                      Retour à l'accueil
                    </Link>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* ✅ Message d'erreur avec ref pour le scroll */}
                    {errors.submit && (
                      <div ref={messageRef} className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
                        <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
                        <p className="text-red-700">{errors.submit}</p>
                      </div>
                    )}

                    {/* Objet */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Objet *</label>
                      <select
                        name="objet"
                        className={`input ${errors.objet ? 'border-red-500' : ''}`}
                        value={formData.objet}
                        onChange={handleInputChange}
                      >
                        <option value="">Sélectionnez un objet</option>
                        {objetOptions.map(option => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                      {/* ✅ Erreur en rouge */}
                      {errors.objet && <p className="text-sm text-red-500 mt-1">{errors.objet}</p>}
                    </div>

                    {/* Nom / Prénom */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Prénom *</label>
                        <input
                          type="text"
                          name="prenom"
                          className={`input ${errors.prenom ? 'border-red-500' : ''}`}
                          value={formData.prenom}
                          onChange={handleInputChange}
                        />
                        {/* ✅ Erreur en rouge */}
                        {errors.prenom && <p className="text-sm text-red-500 mt-1">{errors.prenom}</p>}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                        <input
                          type="text"
                          name="nom"
                          className={`input ${errors.nom ? 'border-red-500' : ''}`}
                          value={formData.nom}
                          onChange={handleInputChange}
                        />
                        {/* ✅ Erreur en rouge */}
                        {errors.nom && <p className="text-sm text-red-500 mt-1">{errors.nom}</p>}
                      </div>
                    </div>

                    {/* Email / Téléphone */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                        <input
                          type="email"
                          name="email"
                          className={`input ${errors.email ? 'border-red-500' : ''}`}
                          value={formData.email}
                          onChange={handleInputChange}
                        />
                        {/* ✅ Erreur en rouge */}
                        {errors.email && <p className="text-sm text-red-500 mt-1">{errors.email}</p>}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                        <input
                          type="tel"
                          name="telephone"
                          className="input"
                          value={formData.telephone}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    {/* Société */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Société</label>
                      <input
                        type="text"
                        name="societe"
                        className="input"
                        value={formData.societe}
                        onChange={handleInputChange}
                      />
                    </div>

                    {/* Message */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                      <textarea
                        name="message"
                        rows={5}
                        className={`input ${errors.message ? 'border-red-500' : ''}`}
                        value={formData.message}
                        onChange={handleInputChange}
                        placeholder="Décrivez votre projet, vos besoins ou vos questions..."
                      />
                      {/* ✅ Erreur en rouge */}
                      {errors.message && <p className="text-sm text-red-500 mt-1">{errors.message}</p>}
                    </div>

                    {/* Pièce jointe conditionnelle */}
                    {isPieceJointeAllowed() && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Pièce jointe {formData.objet === 'Recrutement' ? '(CV recommandé)' : '(optionnelle)'}
                        </label>
                        <FileUpload
                          onFileChange={handleFileChange}
                          acceptedTypes={formData.objet === 'Recrutement' ? ['.pdf', '.doc', '.docx'] : ['.pdf', '.doc', '.docx', '.txt', '.png', '.jpg', '.jpeg']}
                          maxSize={5}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Formats acceptés : {formData.objet === 'Recrutement' ? 'PDF, DOC, DOCX' : 'PDF, DOC, DOCX, TXT, PNG, JPG'} - Max 5 MB
                        </p>
                      </div>
                    )}

                    {/* ✅ Captcha avec erreur en rouge */}
                    <div>
                      <ReCaptcha onVerify={handleCaptchaVerify} />
                      {errors.captcha && (
                        <p className="text-sm text-red-500 mt-2 flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" />
                          {errors.captcha}
                        </p>
                      )}
                    </div>

                    {/* ✅ Consentement RGPD avec erreur en rouge */}
                    <div className="space-y-2">
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          name="consentement_rgpd"
                          id="consentement_rgpd"
                          className={`mt-1 ${errors.consentement_rgpd ? 'border-red-500' : ''}`}
                          checked={formData.consentement_rgpd}
                          onChange={handleInputChange}
                        />
                        <label htmlFor="consentement_rgpd" className="text-sm text-gray-700 leading-relaxed">
                          J'accepte que mes données soient utilisées pour me recontacter dans le cadre de ma demande. 
                          Conformément au RGPD, vous pouvez exercer vos droits en consultant notre{' '}
                          <Link to="/privacy" className="text-primary hover:underline">
                            politique de confidentialité
                          </Link>. *
                        </label>
                      </div>
                      {errors.consentement_rgpd && (
                        <p className="text-sm text-red-500 flex items-center gap-1 ml-6">
                          <AlertCircle className="w-4 h-4" />
                          {errors.consentement_rgpd}
                        </p>
                      )}
                    </div>

                    {/* Bouton d'envoi */}
                    <div className="text-center pt-4">
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="btn-primary px-8 py-3 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                      >
                        {isSubmitting ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            Envoi en cours...
                          </>
                        ) : (
                          <>
                            <Send className="w-5 h-5" />
                            Envoyer ma demande
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>
    </PageTransition>
  );
};

export default Contact;